import { useState, useEffect, useCallback } from "react";
import {
  Button,
  Row,
  Col,
  Tag,
  Modal,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Select,
  Switch,
  Spin,
  Tooltip,
  Popconfirm,
} from "antd";
import {
  Plus,
  Edit,
  Trash2,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  ShieldCheck,
} from "lucide-react";
import dayjs from "dayjs";
import useAuthStore from "stores/AuthStore/AuthStore";
import {
  getMembershipOffering,
  createTier,
  updateTier,
  deleteTier,
  createPlan,
  updatePlan,
  deletePlan,
} from "services/memberships.service";
import {
  renderErrorNotifications,
  renderSuccessNotifications,
} from "helpers/error.helpers";

const MAX_TIERS = 2;
const MAX_PLANS_PER_TIER = { FREE: 1, PAID: 2 };

const BILLING_OPTIONS = [
  { value: "FREE", label: "Free" },
  { value: "SUBSCRIPTION", label: "Subscription" },
];

const DURATION_OPTIONS = [
  { value: 30, label: "Monthly" },
  { value: 365, label: "Annually" },
];

const TIER_TYPE_OPTIONS = [
  { value: "FREE", label: "Free" },
  { value: "PAID", label: "Paid" },
];

function PlanModal({
  open,
  onClose,
  communityId,
  tierId,
  tierType,
  tierName,
  plan,
  onSaved,
}) {
  const [form] = Form.useForm();
  const { userData } = useAuthStore();
  const [saving, setSaving] = useState(false);
  const billingType = Form.useWatch("billing_type", form);

  // billing_type is locked to the tier's type: FREE tier → FREE, PAID tier → SUBSCRIPTION
  const inferredBilling = tierType === "FREE" ? "FREE" : "SUBSCRIPTION";

  const handleValuesChange = (changed) => {
    if (!plan && ("billing_type" in changed || "duration_days" in changed)) {
      const bt = form.getFieldValue("billing_type");
      const dd = form.getFieldValue("duration_days");
      form.setFieldsValue({ plan_code: derivePlanCode(tierName, bt, dd) });
    }
  };

  useEffect(() => {
    if (open) {
      form.resetFields();
      if (plan) {
        form.setFieldsValue({
          ...plan,
          valid_from: plan.valid_from ? dayjs(plan.valid_from) : null,
          valid_until: plan.valid_until ? dayjs(plan.valid_until) : null,
        });
      } else {
        form.setFieldsValue({
          billing_type: inferredBilling,
          price_amount: inferredBilling === "FREE" ? 0 : undefined,
          duration_days: inferredBilling === "FREE" ? null : undefined,
          plan_code: derivePlanCode(
            tierName,
            inferredBilling,
            inferredBilling === "FREE" ? null : undefined
          ),
          valid_from: dayjs(),
        });
      }
    }
  }, [open, plan, form, inferredBilling, tierName]);

  const handleSave = async () => {
    const values = await form.validateFields();
    setSaving(true);
    const { valid_from, valid_until, ...rest } = values;

    // Build body: for create include all fields; for update only schema-allowed fields
    const body = plan
      ? {
          ...(rest.name && { name: rest.name }),
          ...(rest.description && { description: rest.description }),
          ...(rest.price_amount &&
            rest.price_amount !== plan.price_amount && {
              price_amount: rest.price_amount,
            }),
          ...(rest.duration_days && { duration_days: rest.duration_days }),
          ...(rest.is_recommended && { is_recommended: rest.is_recommended }),
          ...(rest.badges?.length && { badges: rest.badges }),
          ...(rest.sort_order && { sort_order: rest.sort_order }),
          ...(valid_until && { valid_until: valid_until.format("YYYY-MM-DD") }),
          audit: { updated_by: userData.email },
        }
      : {
          ...rest,
          currency: "INR",
          price_amount: billingType === "FREE" ? 0 : rest.price_amount,
          valid_from: valid_from?.format("YYYY-MM-DD"),
          ...(valid_until && { valid_until: valid_until.format("YYYY-MM-DD") }),
          audit: { updated_by: userData.email },
        };

    const { errors } = plan
      ? await updatePlan(communityId, tierId, plan.id, body)
      : await createPlan(communityId, tierId, body);
    setSaving(false);
    if (errors?.length) {
      renderErrorNotifications(errors);
      return;
    }
    renderSuccessNotifications({
      title: plan ? "Plan updated" : "Plan created",
    });
    onSaved();
    onClose();
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={plan ? "Edit Plan" : "Add Plan"}
      width={560}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="save" type="primary" loading={saving} onClick={handleSave}>
          {plan ? "Save Changes" : "Create Plan"}
        </Button>,
      ]}
    >
      <Form
        form={form}
        layout="vertical"
        className="pt-2"
        onValuesChange={handleValuesChange}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="plan_code"
              label={
                <span>
                  Plan Code{" "}
                  {!plan && (
                    <span
                      style={{
                        color: "rgba(0,0,0,0.35)",
                        fontSize: 11,
                        fontWeight: 400,
                      }}
                    >
                      (auto-generated)
                    </span>
                  )}
                </span>
              }
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: "Plan code is required",
                },
              ]}
            >
              <Input placeholder="e.g. MONTHLY_BASIC" disabled maxLength={50} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="name"
              label="Name"
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: "Name is required",
                },
                { max: 50 },
              ]}
            >
              <Input
                placeholder="e.g. Monthly Basic"
                maxLength={50}
                showCount
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="description"
          label="Description"
          rules={[{ max: 200 }]}
        >
          <Input.TextArea rows={2} maxLength={200} showCount />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="billing_type"
              label={
                !plan ? (
                  <span>
                    Billing Type{" "}
                    <span
                      style={{
                        color: "rgba(0,0,0,0.35)",
                        fontSize: 11,
                        fontWeight: 400,
                      }}
                    >
                      (set by tier type)
                    </span>
                  </span>
                ) : (
                  "Billing Type"
                )
              }
              rules={[{ required: true }]}
            >
              <Select options={BILLING_OPTIONS} disabled />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="price_amount"
              label="Price"
              rules={
                billingType === "FREE"
                  ? []
                  : [{ required: true, message: "Price is required" }]
              }
            >
              <InputNumber
                min={0}
                step={0.01}
                prefix="₹"
                style={{ width: "100%" }}
                disabled={billingType === "FREE"}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          {!plan && (
            <Col>
              <Form.Item
                name="duration_days"
                label="Duration"
                rules={
                  billingType === "SUBSCRIPTION"
                    ? [{ required: true, message: "Duration is required" }]
                    : []
                }
              >
                <Select
                  options={DURATION_OPTIONS}
                  placeholder="Select duration"
                  disabled={billingType === "FREE"}
                  style={{ minWidth: "100%" }}
                />
              </Form.Item>
            </Col>
          )}
          <Col>
            <Form.Item name="sort_order" label="Sort Order">
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col>
            <Form.Item
              name="is_recommended"
              label="Recommended"
              valuePropName="checked"
            >
              <Switch
                style={{ minWidth: "100%" }}
                checkedChildren="Yes"
                unCheckedChildren="No"
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="badges"
          label="Badges"
          rules={[
            {
              validator(_, value) {
                if (!value) return Promise.resolve();
                if ((value ?? []).length > 5) {
                  return Promise.reject(new Error("Maximum 5 badges allowed"));
                }
                if ((value ?? []).some((v) => v.trim().length > 30)) {
                  return Promise.reject(
                    new Error("Each badge must be 30 characters or fewer")
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Select
            mode="tags"
            placeholder="e.g. Popular, Best Value"
            open={false}
            tokenSeparators={[","]}
            maxTagCount={5}
          />
        </Form.Item>

        <Row gutter={16}>
          {!plan && (
            <Col span={12}>
              <Form.Item
                name="valid_from"
                label="Valid From"
                rules={[{ required: true }]}
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          )}
          <Col span={plan ? 24 : 12}>
            <Form.Item name="valid_until" label="Valid Until">
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}

function derivePlanCode(tierName, billingType, durationDays) {
  const base = (tierName || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
  if (!base) return "";
  if (billingType === "FREE") return `${base}_FREE`;
  if (billingType === "SUBSCRIPTION") {
    if (durationDays === 365) return `${base}_SUBSCRIPTION_ANNUAL`;
    if (durationDays === 30) return `${base}_SUBSCRIPTION_MONTHLY`;
    return `${base}_SUBSCRIPTION`;
  }
  return base;
}

// Edit-only TierModal (no plan step needed when editing)
function TierModal({ open, onClose, communityId, tier, onSaved }) {
  const [form] = Form.useForm();
  const { userData } = useAuthStore();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      form.resetFields();
      form.setFieldsValue(
        tier
          ? {
              ...tier,
              valid_from: tier.valid_from ? dayjs(tier.valid_from) : null,
              valid_until: tier.valid_until ? dayjs(tier.valid_until) : null,
            }
          : { valid_from: dayjs(), tier_type: "PAID" }
      );
    }
  }, [open, tier, form]);

  const handleSave = async () => {
    const values = await form.validateFields();
    setSaving(true);
    const { valid_from, valid_until, features, ...rest } = values;
    const body = {
      ...rest,
      ...(features?.length && { features }),
      ...(!tier ? { valid_from: valid_from?.format("YYYY-MM-DD") } : {}),
      ...(valid_until ? { valid_until: valid_until.format("YYYY-MM-DD") } : {}),
      audit: { updated_by: userData.email },
    };
    const { errors } = tier
      ? await updateTier(communityId, tier.id, body)
      : await createTier(communityId, body);
    setSaving(false);
    if (errors?.length) {
      renderErrorNotifications(errors);
      return;
    }
    renderSuccessNotifications({
      title: tier ? "Tier updated" : "Tier created",
    });
    onSaved();
    onClose();
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="Edit Tier"
      width={560}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="save" type="primary" loading={saving} onClick={handleSave}>
          Save Changes
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical" className="pt-2">
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="name"
              label="Tier Name"
              rules={[{ required: true, whitespace: true }, { max: 50 }]}
            >
              <Input placeholder="e.g. Gold" maxLength={50} showCount />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="display_name"
              label="Display Name"
              rules={[{ max: 50 }]}
            >
              <Input placeholder="e.g. Gold Member" maxLength={50} showCount />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="tier_type"
              label="Tier Type"
              rules={[{ required: true, message: "Tier type is required" }]}
            >
              <Select options={TIER_TYPE_OPTIONS} disabled={!!tier} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="ticket_percentage_discount"
              label="Ticket Discount (%)"
              rules={[
                {
                  validator(_, value) {
                    if (!value) return Promise.resolve();
                    return Number(value) >= 0 && Number(value) <= 100
                      ? Promise.resolve()
                      : Promise.reject(new Error("Must be between 0 and 100"));
                  },
                },
              ]}
            >
              <InputNumber style={{ width: "100%" }} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="description"
          label="Description"
          rules={[{ max: 200 }]}
        >
          <Input.TextArea rows={2} maxLength={200} showCount />
        </Form.Item>

        <Form.Item name="features" label="Features">
          <Select
            mode="tags"
            placeholder="Type a feature and press Enter"
            open={false}
          />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="sort_order" label="Sort Order">
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          {tier && (
            <Col span={12}>
              <Form.Item
                name="is_active"
                label="Active"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          )}
        </Row>

        <Row gutter={16}>
          {!tier && (
            <Col span={12}>
              <Form.Item
                name="valid_from"
                label="Valid From"
                rules={[{ required: true }]}
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          )}
          <Col span={tier ? 24 : 12}>
            <Form.Item name="valid_until" label="Valid Until">
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}

// Create-only wizard: Step 1 = Tier details, Step 2 = First plan
function CreateTierWizard({ open, onClose, communityId, onSaved }) {
  const [step, setStep] = useState(0);
  const [tierForm] = Form.useForm();
  const [planForm] = Form.useForm();
  const { userData } = useAuthStore();
  const [saving, setSaving] = useState(false);

  const tierName = Form.useWatch("name", tierForm);
  const billingType = Form.useWatch("billing_type", planForm);

  const handlePlanValuesChange = (changed) => {
    const keys = Object.keys(changed);
    if (keys.includes("billing_type") || keys.includes("duration_days")) {
      const bt = planForm.getFieldValue("billing_type");
      const dd = planForm.getFieldValue("duration_days");
      planForm.setFieldsValue({ plan_code: derivePlanCode(tierName, bt, dd) });
    }
  };

  useEffect(() => {
    if (open) {
      setStep(0);
      tierForm.resetFields();
      planForm.resetFields();
      tierForm.setFieldsValue({ valid_from: dayjs(), tier_type: "PAID" });
      planForm.setFieldsValue({
        billing_type: "SUBSCRIPTION",
        valid_from: dayjs(),
      });
    }
  }, [open, tierForm, planForm]);

  const handleNext = async () => {
    await tierForm.validateFields();
    // Map tier_type → billing_type: FREE tier always has FREE billing, PAID gets SUBSCRIPTION
    const inferredBilling =
      tierForm.getFieldValue("tier_type") === "FREE" ? "FREE" : "SUBSCRIPTION";
    const dd =
      inferredBilling === "FREE"
        ? null
        : planForm.getFieldValue("duration_days");
    planForm.setFieldsValue({
      billing_type: inferredBilling,
      duration_days: dd,
      price_amount:
        inferredBilling === "FREE" ? 0 : planForm.getFieldValue("price_amount"),
      plan_code: derivePlanCode(
        tierForm.getFieldValue("name"),
        inferredBilling,
        dd
      ),
    });
    setStep(1);
  };

  const handleBack = () => setStep(0);

  const handleFinish = async () => {
    const planValues = await planForm.validateFields();
    setSaving(true);

    // Step 1: create the tier
    const tierValues = tierForm.getFieldsValue(true);
    const { valid_from: tVf, valid_until: tVu, ...tierRest } = tierValues;
    const { data: newTier, errors: tierErrors } = await createTier(
      communityId,
      {
        ...tierRest,
        valid_from: tVf?.format("YYYY-MM-DD"),
        ...(tVu ? { valid_until: tVu.format("YYYY-MM-DD") } : {}),
        audit: { updated_by: userData.email },
      }
    );

    if (tierErrors?.length) {
      setSaving(false);
      renderErrorNotifications(tierErrors);
      setStep(0);
      return;
    }

    // Step 2: create the first plan under the new tier
    const { valid_from: pVf, valid_until: pVu, ...planRest } = planValues;
    const { errors: planErrors } = await createPlan(
      communityId,
      newTier.tier_id,
      {
        ...planRest,
        currency: "INR",
        price_amount: billingType === "FREE" ? 0 : planRest.price_amount,
        valid_from: pVf?.format("YYYY-MM-DD"),
        ...(pVu ? { valid_until: pVu.format("YYYY-MM-DD") } : {}),
        audit: { updated_by: userData.email },
      }
    );

    setSaving(false);

    if (planErrors?.length) {
      renderErrorNotifications(planErrors);
      // Tier was already created — reload so it shows up even without the plan
      onSaved();
      onClose();
      return;
    }

    renderSuccessNotifications({ title: "Tier and plan created" });
    onSaved();
    onClose();
  };

  const stepTitles = ["Create Tier", "Add First Plan"];

  const footer =
    step === 0
      ? [
          <Button key="cancel" onClick={onClose}>
            Cancel
          </Button>,
          <Button key="next" type="primary" onClick={handleNext}>
            Next: Add Plan →
          </Button>,
        ]
      : [
          <Button key="back" onClick={handleBack}>
            ← Back
          </Button>,
          <Button
            key="finish"
            type="primary"
            loading={saving}
            onClick={handleFinish}
          >
            Create Tier &amp; Plan
          </Button>,
        ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        <div>
          <div style={{ fontSize: 16, fontWeight: 600 }}>
            {stepTitles[step]}
          </div>
          <div
            style={{
              fontSize: 12,
              color: "rgba(247, 241, 241, 0.45)",
              fontWeight: 400,
              marginTop: 2,
            }}
          >
            Step {step + 1} of 2
          </div>
        </div>
      }
      width={600}
      footer={footer}
      destroyOnClose
    >
      {/* ── Step 1: Tier details ── */}
      <div style={{ display: step === 0 ? "block" : "none" }}>
        <Form form={tierForm} layout="vertical" className="pt-2">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Tier Name"
                rules={[{ required: true, whitespace: true }, { max: 50 }]}
              >
                <Input placeholder="e.g. Gold" maxLength={50} showCount />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="display_name"
                label="Display Name"
                rules={[{ max: 50 }]}
              >
                <Input
                  placeholder="e.g. Gold Member"
                  maxLength={50}
                  showCount
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="tier_type"
                label="Tier Type"
                rules={[{ required: true, message: "Tier type is required" }]}
              >
                <Select options={TIER_TYPE_OPTIONS} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="ticket_percentage_discount"
                label="Ticket Discount (%)"
                rules={[
                  {
                    validator(_, value) {
                      if (!value) return Promise.resolve();
                      return Number(value) >= 0 && Number(value) <= 100
                        ? Promise.resolve()
                        : Promise.reject(
                            new Error("Must be between 0 and 100")
                          );
                    },
                  },
                ]}
              >
                <InputNumber style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ max: 200 }]}
          >
            <Input.TextArea rows={2} maxLength={200} showCount />
          </Form.Item>

          <Form.Item
            name="features"
            label="Features (Max 5)"
            rules={[
              {
                validator(_, value) {
                  if (!value) return Promise.resolve();
                  if ((value ?? []).length > 5) {
                    return Promise.reject(
                      new Error("Maximum 5 features allowed.")
                    );
                  }
                  if (value.some((v) => v.trim().length > 30)) {
                    return Promise.reject(
                      new Error("Each feature must be 30 characters or fewer.")
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Select
              mode="tags"
              placeholder="Type a feature and press Enter"
              open={false}
              tokenSeparators={[","]}
              maxTagCount={5}
            />
          </Form.Item>

          {/* <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="sort_order" label="Sort Order">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row> */}

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="valid_from"
                label="Valid From"
                rules={[{ required: true }]}
              >
                <DatePicker style={{ width: "100%" }} disabled={true} />
              </Form.Item>
            </Col>
            {/* <Col span={12}>
              <Form.Item name="valid_until" label="Valid Until">
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col> */}
          </Row>
        </Form>
      </div>

      {/* ── Step 2: First plan ── */}
      <div style={{ display: step === 1 ? "block" : "none" }}>
        <Form
          form={planForm}
          layout="vertical"
          className="pt-2"
          onValuesChange={handlePlanValuesChange}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="plan_code"
                label="Plan Code"
                tooltip="Auto-generated from Tier Name, Billing Type, and Duration. You can edit it."
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: "Plan code is required",
                  },
                ]}
              >
                <Input
                  disabled={true}
                  placeholder="e.g. GOLD_SUBSCRIPTION_MONTHLY"
                  maxLength={50}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Plan Name"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: "Name is required",
                  },
                  { max: 50 },
                ]}
              >
                <Input
                  placeholder="e.g. Monthly Basic"
                  maxLength={50}
                  showCount
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ max: 200 }]}
          >
            <Input.TextArea rows={2} maxLength={200} showCount />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="billing_type"
                label={
                  <span>
                    Billing Type{" "}
                    <span
                      style={{
                        color: "rgba(227, 217, 217, 0.35)",
                        fontSize: 11,
                        fontWeight: 400,
                      }}
                    >
                      (set by tier type)
                    </span>
                  </span>
                }
                rules={[{ required: true }]}
              >
                <Select options={BILLING_OPTIONS} disabled />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="price_amount"
                label="Price"
                rules={
                  billingType === "FREE"
                    ? []
                    : [
                        { required: true, message: "", whitespace: true },
                        {
                          validator(_, value) {
                            return Number(value) >= 0 &&
                              Number(value) <= 9999999
                              ? Promise.resolve()
                              : Promise.reject(
                                  new Error(
                                    "Price must be between 0 and 9999999"
                                  )
                                );
                          },
                        },
                      ]
                }
              >
                <Input
                  prefix="₹"
                  inputMode="numeric"
                  style={{ width: "100%" }}
                  disabled={billingType === "FREE"}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="duration_days"
                label="Duration"
                rules={
                  billingType === "SUBSCRIPTION"
                    ? [{ required: true, message: "Duration is required" }]
                    : []
                }
              >
                <Select
                  options={DURATION_OPTIONS}
                  placeholder="Select duration"
                  disabled={billingType === "FREE"}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            {/* <Col span={12}>
              <Form.Item name="sort_order" label="Sort Order">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col> */}
            <Col span={12}>
              <Form.Item
                name="valid_from"
                label="Valid From"
                rules={[{ required: true }]}
              >
                <DatePicker style={{ width: "100%" }} disabled={true} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </div>
    </Modal>
  );
}

function TierCard({ tier, communityId, onRefresh }) {
  const [expanded, setExpanded] = useState(true);
  const [editTierOpen, setEditTierOpen] = useState(false);
  const [planModal, setPlanModal] = useState({ open: false, plan: null });

  const planCount = tier.plans?.length ?? 0;
  const maxPlans = MAX_PLANS_PER_TIER[tier.tier_type] ?? 2;
  const planLimitReached = planCount >= maxPlans;

  const handleDeleteTier = async () => {
    const activePlans = (tier.plans ?? []).filter((p) => p.is_active);
    for (const plan of activePlans) {
      const { errors } = await deletePlan(communityId, tier.id, plan.id);
      if (errors?.length) {
        renderErrorNotifications(errors);
        return;
      }
    }
    const { errors } = await deleteTier(communityId, tier.id);
    if (errors?.length) {
      renderErrorNotifications(errors);
      return;
    }
    renderSuccessNotifications({ title: "Tier deactivated" });
    onRefresh();
  };

  const handleDeactivatePlan = async (planId) => {
    const { errors } = await deletePlan(communityId, tier.id, planId);
    if (errors?.length) {
      renderErrorNotifications(errors);
      return;
    }
    renderSuccessNotifications({ title: "Plan deactivated" });
    onRefresh();
  };

  return (
    <div className="rounded-2xl border border-border/50 bg-card overflow-hidden shadow-card">
      {/* Tier header */}
      <div className="p-5 border-b bg-muted/20 flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-bold text-base">{tier.name}</h3>
            {tier.display_name && <Tag>{tier.display_name}</Tag>}
            {tier.ticket_percentage_discount > 0 && (
              <Tag color="green">
                {tier.ticket_percentage_discount}% ticket discount
              </Tag>
            )}
          </div>
          {tier.description && (
            <p className="text-sm text-muted-foreground mt-1">
              {tier.description}
            </p>
          )}
          {tier.features?.length > 0 && (
            <ul className="mt-2 space-y-1">
              {tier.features.map((f, i) => (
                <li
                  key={i}
                  className="flex items-center gap-1.5 text-xs text-muted-foreground"
                >
                  <CheckCircle2 className="h-3 w-3 text-emerald-500 shrink-0" />{" "}
                  {f}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Tooltip title="Edit tier">
            <Button
              size="small"
              icon={<Edit className="h-3.5 w-3.5" />}
              onClick={() => setEditTierOpen(true)}
            />
          </Tooltip>
          <Popconfirm
            title="Deactivate this tier?"
            description={
              tier.plans?.length > 0
                ? `This will first deactivate all ${tier.plans.length} plan(s) under this tier, then deactivate the tier. Plans with active memberships cannot be deactivated.`
                : "This tier will be deactivated and no longer visible to members."
            }
            onConfirm={handleDeleteTier}
            okText="Yes, Deactivate"
            okType="danger"
          >
            <Tooltip title="Delete tier">
              <Button
                size="small"
                danger
                icon={<Trash2 className="h-3.5 w-3.5" />}
              />
            </Tooltip>
          </Popconfirm>
          <Button
            size="small"
            icon={
              expanded ? (
                <ChevronUp className="h-3.5 w-3.5" />
              ) : (
                <ChevronDown className="h-3.5 w-3.5" />
              )
            }
            onClick={() => setExpanded((v) => !v)}
          />
        </div>
      </div>

      {/* Plans */}
      {expanded && (
        <div className="p-4 space-y-3">
          {tier.plans?.length > 0 ? (
            <Row gutter={[12, 12]}>
              {tier.plans.map((plan) => (
                <Col key={plan.id} xs={24} sm={12} lg={8}>
                  <div className="rounded-xl border border-border/50 bg-background p-4 space-y-2 relative">
                    {plan.is_recommended && (
                      <Tag
                        color="blue"
                        className="absolute top-2 right-2 text-[10px]"
                      >
                        Recommended
                      </Tag>
                    )}
                    <p className="font-semibold text-sm">{plan.name}</p>
                    <p className="text-xl font-bold">
                      {plan.billing_type === "FREE"
                        ? "Free"
                        : `₹${plan.price_amount}`}
                      {plan.billing_type === "SUBSCRIPTION" &&
                        plan.duration_days && (
                          <span className="text-xs text-muted-foreground font-normal ml-1">
                            / {plan.duration_days === 365 ? "year" : "month"}
                          </span>
                        )}
                    </p>
                    {plan.description && (
                      <p className="text-xs text-muted-foreground">
                        {plan.description}
                      </p>
                    )}
                    <div className="flex gap-1 pt-1">
                      <Button
                        size="small"
                        icon={<Edit className="h-3 w-3" />}
                        onClick={() => setPlanModal({ open: true, plan })}
                      >
                        Edit
                      </Button>
                      <Popconfirm
                        title="Deactivate this plan?"
                        description="Plans with active memberships cannot be deactivated."
                        onConfirm={() => handleDeactivatePlan(plan.id)}
                        okText="Deactivate"
                        okType="danger"
                      >
                        <Button
                          size="small"
                          danger
                          icon={<Trash2 className="h-3 w-3" />}
                        >
                          Deactivate
                        </Button>
                      </Popconfirm>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-3">
              No plans yet.
            </p>
          )}
          <Tooltip
            title={
              planLimitReached
                ? `Maximum ${maxPlans} plan${maxPlans === 1 ? "" : "s"} allowed per ${tier.tier_type?.toLowerCase()} tier`
                : ""
            }
          >
            <Button
              icon={<Plus className="h-3.5 w-3.5" />}
              size="small"
              disabled={planLimitReached}
              onClick={() => setPlanModal({ open: true, plan: null })}
            >
              Add Plan
            </Button>
          </Tooltip>
        </div>
      )}

      <TierModal
        open={editTierOpen}
        onClose={() => setEditTierOpen(false)}
        communityId={communityId}
        tier={tier}
        onSaved={onRefresh}
      />
      <PlanModal
        open={planModal.open}
        onClose={() => setPlanModal({ open: false, plan: null })}
        communityId={communityId}
        tierId={tier.id}
        tierType={tier.tier_type}
        tierName={tier.name}
        plan={planModal.plan}
        onSaved={onRefresh}
      />
    </div>
  );
}

function TiersTab({ communityId }) {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createTierOpen, setCreateTierOpen] = useState(false);

  const loadOffering = useCallback(async () => {
    setLoading(true);
    const { data, errors } = await getMembershipOffering(communityId);
    setLoading(false);
    if (errors?.length) {
      renderErrorNotifications(errors);
      return;
    }
    setTiers(data?.tiers ?? data ?? []);
  }, [communityId]);

  useEffect(() => {
    loadOffering();
  }, [loadOffering]);

  const tierLimitReached = tiers.length >= MAX_TIERS;

  if (loading)
    return (
      <div className="py-20 flex justify-center">
        <Spin size="large" />
      </div>
    );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold">Membership Tiers</h2>
          <p className="text-sm text-muted-foreground">
            Each tier can have multiple pricing plans.
          </p>
        </div>
        <Tooltip
          title={
            tierLimitReached
              ? `Maximum ${MAX_TIERS} tiers allowed per community`
              : ""
          }
        >
          <Button
            type="primary"
            icon={<Plus className="h-4 w-4" />}
            disabled={tierLimitReached}
            onClick={() => setCreateTierOpen(true)}
          >
            Create Tier
          </Button>
        </Tooltip>
      </div>

      {tiers.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center space-y-4 bg-card rounded-2xl border border-dashed">
          <div className="h-16 w-16 rounded-full bg-gradient-to-br from-lavender-100 to-lavender-50 flex items-center justify-center">
            <ShieldCheck className="h-8 w-8 text-gray-600" />
          </div>
          <div className="space-y-1">
            <h3 className="font-bold">No membership tiers yet</h3>
            <p className="text-sm text-muted-foreground">
              Create tiers and add pricing plans to start accepting members.
            </p>
          </div>
          <Button
            type="primary"
            icon={<Plus className="h-4 w-4" />}
            onClick={() => setCreateTierOpen(true)}
          >
            Create First Tier
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {tiers.map((tier) => (
            <TierCard
              key={tier.id}
              tier={tier}
              communityId={communityId}
              onRefresh={loadOffering}
            />
          ))}
        </div>
      )}

      <CreateTierWizard
        open={createTierOpen}
        onClose={() => setCreateTierOpen(false)}
        communityId={communityId}
        onSaved={loadOffering}
      />
    </div>
  );
}

export default TiersTab;
